<script setup lang="ts">
  import { useQuery } from '@tanstack/vue-query'
  import { onKeyStroke, useCloned, useScrollLock, useTimeout, whenever } from '@vueuse/core'
  import { klona } from 'klona/lite'
  import { useData } from 'vike-vue/useData'
  import {
    computed,
    onMounted,
    onServerPrefetch,
    reactive,
    ref,
    toRaw,
    watch,
    watchEffect,
  } from 'vue'
  import { UpdateIslandData } from '~database/turnip-exchange/schema'
  import BannerAd from '~renderer/components/ads/BannerAd.vue'
  import AnonymousLoginModal from '~renderer/components/AnonymousLoginModal.vue'
  import ConfirmBanModal from '~renderer/components/ConfirmBanModal.vue'
  import ConfirmCloseModal from '~renderer/components/ConfirmCloseModal.vue'
  import ConfirmKickModal from '~renderer/components/ConfirmKickModal.vue'
  import ConfirmLeaveModal from '~renderer/components/ConfirmLeaveModal.vue'
  import Footer from '~renderer/components/Footer.vue'
  import GetAnnouncementModal from '~renderer/components/GetAnnouncementModal.vue'
  import Header from '~renderer/components/Header.vue'
  import ManageVisitors from '~renderer/components/ManageVisitors.vue'
  import PastVisitors from '~renderer/components/PastVisitors.vue'
  import RateVisitModal from '~renderer/components/RateVisitModal.vue'
  import ReportUserModal from '~renderer/components/ReportUserModal.vue'
  import SetAnnouncementModal from '~renderer/components/SetAnnouncementModal.vue'
  import ShareIslandModal from '~renderer/components/ShareIslandModal.vue'
  import StarRatingDisplay from '~renderer/components/StarRatingDisplay.vue'
  import UpdateIslandModal from '~renderer/components/UpdateIslandModal.vue'
  import { useAuth } from '~renderer/composables/useAuth'
  import { useIslandTime } from '~renderer/composables/useIslandTime'
  import { useTurnipExchangeIslandWebSocket } from '~renderer/composables/useTurnipExchangeIslandWebSocket'
  import { clampTurnipPrice, typeBorders, typeColors } from '~renderer/util/island'
  import { playDing } from '~renderer/util/sfx'
  import { guard } from '~renderer/util/telefunc'
  import { onCloseIsland } from '~rpc/procedures/island/onCloseIsland.telefunc'
  import { onGetIslandVisitorHistory } from '~rpc/procedures/island/onGetIslandVisitorHistory.telefunc'
  import { onGetQueuedUsers } from '~rpc/procedures/island/onGetQueuedUsers.telefunc'
  import { onJoinIslandQueue } from '~rpc/procedures/island/onJoinIslandQueue.telefunc'
  import { onLeaveIslandQueue } from '~rpc/procedures/island/onLeaveIslandQueue.telefunc'
  import { onPauseIsland } from '~rpc/procedures/island/onPauseIsland.telefunc'
  import { onRateIsland } from '~rpc/procedures/island/onRateIsland.telefunc'
  import { onRemoveQueuedUser } from '~rpc/procedures/island/onRemoveQueuedUser.telefunc'
  import { onResumeIsland } from '~rpc/procedures/island/onResumeIsland.telefunc'
  import { onSetAnnouncement } from '~rpc/procedures/island/onSetAnnouncement.telefunc'
  import { onUpdateIsland } from '~rpc/procedures/island/onUpdateIsland.telefunc'
  import { onBanUserById } from '~rpc/procedures/user/onBanUserById.telefunc'
  import { onBlockUserById } from '~rpc/procedures/user/onBlockUserById.telefunc'
  import { onRateUser } from '~rpc/procedures/user/onRateUser.telefunc'
  import { onReportUser } from '~rpc/procedures/user/onReportUser.telefunc'
  import * as AttemptUtil from '~shared/util/attempt'
  import { unscramble } from '~shared/util/dodo-code'
  import type { IslandPageData } from './+data'

  type Modals = {
    anonymousLogin: boolean
    reportUser:
      | false
      | {
          userId: string
          name: string
          visitId: string
        }
    rateVisit: boolean
    confirmLeave: boolean
    confirmKick:
      | false
      | {
          userId: string
          name: string
          visitId: string | null
        }
    confirmBan:
      | false
      | {
          userId: string
          name: string
        }
    confirmClose: boolean
    shareIsland: boolean
    updateIsland: boolean
    setAnnouncement: boolean
    getAnnouncement: boolean
  }

  // MARK: Pure Composables
  const { island, hostUsername, userState } = useData<IslandPageData>()
  const { user, requiresUsernameReset, isAdmin, isModerator } = useAuth()
  const { timeAgo, getIslandTime } = useIslandTime()
  const isScrollLocked = useScrollLock(typeof window !== 'undefined' ? document.body : null)

  const { start: preventRejoin, isPending: isRejoinPrevented } = useTimeout(5000, {
    controls: true,
    immediate: false,
  })
  const { start: preventRelock, isPending: isRelockPrevented } = useTimeout(5000, {
    controls: true,
    immediate: false,
  })

  // MARK: State
  const ticket = ref(userState?.ticket)
  const dodoCode = ref<string | undefined>()
  const visitId = ref(userState?.visitId)
  const hostTabIndex = ref(0)
  const lockChangeWorking = ref(false)
  const rateVisitTarget = ref<string | undefined>()
  const modals = reactive<Modals>({
    anonymousLogin: false,
    reportUser: false,
    rateVisit: false,
    confirmLeave: false,
    confirmKick: false,
    confirmBan: false,
    confirmClose: false,
    shareIsland: false,
    updateIsland: false,
    setAnnouncement: false,
    getAnnouncement: false,
  })

  // MARK: Computed
  const modalIsOpen = computed(() => Object.values(modals).some(Boolean))
  const fruitName = computed(() => titleTextOf(island?.fruit || ''))
  const typeName = computed(() => titleTextOf(island?.type || ''))
  const npcIcons = computed(() => (island?.npc || []).map(iconOf))
  const isIslandAdmin = computed(
    () => user.value?.id === island?.hostId || isAdmin.value || isModerator.value,
  )
  const canBanUsers = computed(() => isAdmin.value || isModerator.value)
  const hemisphereName = computed(() => titleTextOf(island?.hemisphere || ''))
  const bellsFee = computed(() => {
    if (!island || !island?.entryFee?.length) return

    const bellsFeeItem = island.entryFee.flat().find((fee) => fee?.type === 'bells')

    return bellsFeeItem?.amount
  })
  const nmtFee = computed(() => {
    if (!island || !island?.entryFee?.length) return

    const nmtFeeItem = island.entryFee.flat().find((fee) => fee?.type === 'nmt')

    return nmtFeeItem?.amount
  })
  const hasModifiedSwitch = computed(() => island && island.turnipPrice >= 990)

  const wsUrl = computed(() => {
    if (typeof window === 'undefined') return
    if (!island?.turnipCode) return
    if (!ticket.value && !isIslandAdmin.value) return
    const { protocol, host } = window.location
    if (!host) return
    if (!protocol) return

    const url = new URL(window.location.href)
    url.protocol = protocol.replace('http', 'ws')
    url.pathname = `/ws/island/${island.turnipCode}`
    if (ticket.value) url.searchParams.set('ticket', ticket.value)

    return url.toString()
  })

  const visitors = computed(
    () =>
      queuedUsers.value
        ?.filter((v) => v.eligibleAt)
        .sort((a, b) => b.position.localeCompare(a.position)) || [],
  )

  const usersInLine = computed(
    () =>
      queuedUsers.value
        ?.filter((v) => !visitors.value.includes(v))
        .sort((a, b) => a.position.localeCompare(b.position)) || [],
  )

  const currentPosition = computed(() => {
    if (!island) return undefined
    const position = usersInLine.value.findIndex((v) => v.userId === user.value?.id)

    return position !== -1 ? Math.max(1, position + 1) : undefined
  })

  const isEligible = computed(() => !!visitors.value?.find((v) => v.userId === user.value?.id))

  const rating = computed(() => island?.averageRating || 0)

  // const hasClaimed = computed(() => {
  //   if (!user.value) return false

  //   return visitors.value.some((v) => v.userId === user.value?.id && v.claimedAt)
  // })

  // MARK: Dependent Composables
  const { open, close, send, data: socketData } = useTurnipExchangeIslandWebSocket(wsUrl)
  const {
    data: _queuedUsers,
    refetch,
    suspense: suspenseQueuedUsers,
  } = useQuery({
    queryKey: ['queued-users', island?.turnipCode],
    queryFn: async () => {
      if (!island?.turnipCode) return []

      return await guard(
        onGetQueuedUsers({
          turnipCode: island.turnipCode,
        }),
      )
    },
    staleTime: Infinity,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })
  const { cloned: queuedUsers } = useCloned(_queuedUsers, { clone: klona })

  const {
    data: pastVisits,
    refetch: refetchPastVisits,
    suspense: suspensePastVisits,
  } = useQuery({
    queryKey: ['past-visits', island?.islandId],
    queryFn: async () => {
      if (!island?.islandId) return []
      if (!isIslandAdmin.value) return []

      return await guard(
        onGetIslandVisitorHistory({
          islandId: island.islandId,
        }),
      )
    },
    staleTime: Infinity,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  // MARK: Methods
  const iconOf = (
    item: NonNullable<NonNullable<typeof island>['npc']>[number],
  ): { path: string; alt: string } => {
    const npcImageName = item.replace(/ /g, '-').replaceAll('.', '').toLowerCase()

    return { path: `/images/npc/${npcImageName}.svg`, alt: item }
  }

  const titleTextOf = (str: string): string =>
    `${str.slice(0, 1).toUpperCase()}${str.slice(1).toLowerCase()}`

  const escapeModals = () => {
    modals.reportUser = false
    modals.confirmLeave = false
    modals.confirmKick = false
    modals.confirmBan = false
    modals.confirmClose = false
    modals.shareIsland = false
    modals.updateIsland = false
    modals.setAnnouncement = false
    modals.getAnnouncement = false
  }

  const onShareIslandClicked = async () => {
    if (!island || !isIslandAdmin.value) return

    modals.shareIsland = true
  }

  const onShareIslandCancelled = async () => {
    if (!island || !isIslandAdmin.value) return

    modals.shareIsland = false
  }

  const onUpdateIslandClicked = async () => {
    if (!island || !isIslandAdmin.value) return

    modals.updateIsland = true
  }

  const onUpdateIslandConfirmed = async (updateData: UpdateIslandData) => {
    if (!island || !isIslandAdmin.value) return

    const { turnipCode, dodoCode: currentDodoCode } = island
    if (!turnipCode || turnipCode === currentDodoCode) return

    const [err, updatedIsland] = await AttemptUtil.async(() =>
      guard(onUpdateIsland({ islandId: island.islandId, updateIslandRequestData: updateData })),
    )
    if (err) {
      console.error(err)
    } else {
      Object.assign(island, updatedIsland)
    }

    modals.updateIsland = false
  }

  const onUpdateIslandCancelled = async () => {
    if (!island || !isIslandAdmin.value) return

    modals.updateIsland = false
  }

  const onLockQueueClicked = async () => {
    if (!island || !isIslandAdmin.value) return
    if (island.status === 'closed') return

    const newStatus = island.status === 'paused' ? 'open' : 'paused'
    const rpcCall = island.status === 'paused' ? onResumeIsland : onPauseIsland

    lockChangeWorking.value = true
    const [err] = await AttemptUtil.async(() => guard(rpcCall({ islandId: island.islandId })))
    lockChangeWorking.value = false

    if (err) throw err
    island.status = newStatus
    preventRelock()
    await refetch()
  }

  const onJoinQueueClicked = async () => {
    if (!island) return
    if (!user.value) {
      modals.anonymousLogin = true
      return
    }

    const [err, result] = await AttemptUtil.async(() =>
      guard(onJoinIslandQueue({ islandId: island.islandId })),
    )
    if (err) throw err

    ticket.value = result.ticket
    open()

    await refetch()
  }

  const onLeaveQueueClicked = async () => {
    modals.confirmLeave = true
  }

  const onLeaveQueueConfirmed = async () => {
    if (!island) return

    const [err] = await AttemptUtil.async(() =>
      guard(onLeaveIslandQueue({ islandId: island.islandId })),
    )
    if (err) console.error(err)

    modals.confirmLeave = false
  }

  const onLeaveQueueCancelled = () => {
    modals.confirmLeave = false
  }

  const onCloseIslandClicked = async () => {
    modals.confirmClose = true
  }

  const onCloseIslandConfirmed = async () => {
    if (!island) return

    const [err] = await AttemptUtil.async(() => guard(onCloseIsland({ islandId: island.islandId })))
    if (err) console.error(err)

    modals.confirmClose = false
    window.location.href = '/'
  }

  const onCloseIslandCancelled = () => {
    modals.confirmClose = false
  }

  const onReportUserClicked = async (visitId: string) => {
    const visit = pastVisits.value?.find((v) => v.visitId === visitId)
    if (!visit) return

    const { userId, name } = visit
    modals.reportUser = { userId, name, visitId }
  }

  const onReportUserConfirmed = async (
    userId: string,
    visitId: string,
    reportType: string,
    comment: string,
  ) => {
    const [err] = await AttemptUtil.async(() =>
      guard(onReportUser({ userId, visitId, reportType, comment })),
    )
    if (err) console.error(err)

    modals.reportUser = false
  }

  const onReportUserCancelled = async () => {
    modals.reportUser = false
  }

  const onRatingConfirmed = async (rating: number, comment: string) => {
    const targetVisitId = rateVisitTarget.value
    if (!targetVisitId) return

    const [err] = await AttemptUtil.async(() =>
      guard(
        onRateIsland({
          visitId: targetVisitId,
          rating,
          comment,
        }),
      ),
    )
    if (err) console.error(err)

    rateVisitTarget.value = undefined
    modals.rateVisit = false
  }

  const onRateVisitClicked = async (visitId: string, rating: number) => {
    const [err] = await AttemptUtil.async(() =>
      guard(
        onRateUser({
          visitId,
          rating,
        }),
      ),
    )
    if (err) console.error(err)

    refetchPastVisits()
  }

  const onShowDodoCodeClicked = async () => {
    if (!island) return

    // If we already have the data from SSR no need to fetch
    if (userState?.dodoCode) {
      dodoCode.value = userState.dodoCode
      visitId.value = userState.visitId
      return
    }

    send({
      type: 'claim-dodo-code',
    })
  }

  const handleLeaveIsland = async () => {
    rateVisitTarget.value = visitId.value || undefined
    ticket.value = undefined
    dodoCode.value = undefined
    visitId.value = undefined

    close()
    preventRejoin()
    if (rateVisitTarget.value) modals.rateVisit = true

    await refetch()
  }

  const onRemoveUserRequested = async (userId: string, visitId: string | null) => {
    const user = queuedUsers.value?.find((u) => u.userId === userId)
    if (!user) return
    if (visitId && user.visitId !== visitId) return

    const { name } = user
    modals.confirmKick = { userId, name, visitId }
  }

  const onRemoveUserConfirmed = async () => {
    if (!island || !modals.confirmKick) return
    const { userId, visitId } = modals.confirmKick
    const [err] = await AttemptUtil.async(() =>
      guard(onRemoveQueuedUser({ userId, islandId: island?.islandId, visitId })),
    )

    if (err) throw err

    modals.confirmKick = false
    refetch()
  }

  const onBlockUserConfirmed = async () => {
    if (!island || !modals.confirmKick) return
    const { userId } = modals.confirmKick

    const [err] = await AttemptUtil.async(() => guard(onBlockUserById({ userId })))

    if (err) throw err

    modals.confirmKick = false
    refetch()
  }

  const onRemoveUserCancelled = async () => {
    modals.confirmKick = false
  }

  const onBanUserCancelled = async () => {
    modals.confirmBan = false
  }

  const onBanUserRequested = async (userId: string, name: string) => {
    modals.confirmBan = { userId, name }
  }

  const onBanUserConfirmed = async () => {
    if (!island || !modals.confirmBan) return
    const { userId } = modals.confirmBan
    const [err] = await AttemptUtil.async(() => guard(onBanUserById({ userId })))

    if (err) throw err

    modals.confirmBan = false
    refetch()
  }

  const onGetAnnouncementClosed = async () => {
    modals.getAnnouncement = false
  }

  const onSetAnnouncementClicked = async () => {
    modals.setAnnouncement = true
  }

  const onSetAnnouncementConfirmed = async (announcement: string) => {
    if (!island || !modals.setAnnouncement) return
    const { islandId } = island
    const [err] = await AttemptUtil.async(() =>
      guard(onSetAnnouncement({ islandId, announcement })),
    )

    if (err) throw err

    modals.setAnnouncement = false
    island.announcement = announcement
    refetch()
  }

  const onSetAnnouncementCancelled = async () => {
    modals.setAnnouncement = false
  }

  // MARK: Watch
  watch(queuedUsers, (users) => {
    if (isIslandAdmin.value) return
    if (!users) return

    // If user isn't in the queue but have a ticket, they were removed externally (kicked)
    const userInQueue = users.find((v) => v.userId === user.value?.id)
    if (!userInQueue && ticket.value) handleLeaveIsland()
  })
  watchEffect(async () => {
    const { value: message } = socketData

    if (!message) return
    if (message.type === 'turnip-exchange-error') {
      console.error(message.error)
      // handle islands that have been closed since opening the page
      if (message.error.code === 'island/closed') window.location.reload()
    }
    if (message.type === 'queued-users-update') {
      queuedUsers.value = toRaw(message.users)
      refetchPastVisits()
    }
    if (message.type === 'dodo-code') {
      dodoCode.value = unscramble(message.code)
      visitId.value = message.visitId
    }
    if (message.type === 'island-closed') window.location.reload()
    if (message.type === 'island-announcement') {
      island.announcement = message.announcement
      if (user.value?.id !== island?.hostId) modals.getAnnouncement = true
    }
  })
  watchEffect(() => {
    const {
      reportUser,
      rateVisit,
      confirmLeave,
      confirmKick,
      confirmClose,
      shareIsland,
      updateIsland,
    } = modals
    isScrollLocked.value =
      !!reportUser ||
      rateVisit ||
      confirmLeave ||
      !!confirmKick ||
      confirmClose ||
      shareIsland ||
      updateIsland
  })

  onServerPrefetch(async () => Promise.all([suspenseQueuedUsers, suspensePastVisits]))
  onMounted(() => {
    // If the island is closed, nobody should connect to the websocket
    if (island?.status === 'closed') return
    // If you are not the host and you dont have a ticket there is no reason
    // to connect to the websocket
    if (!ticket.value && !isIslandAdmin.value) return

    open()
  })
  whenever(isEligible, () => playDing())
  onKeyStroke('Escape', escapeModals)
</script>

<template>
  <div
    class="bg-tan-900 flex min-h-screen flex-col font-sans text-white lg:h-screen"
    v-if="!requiresUsernameReset"
  >
    <Header />

    <div
      v-if="modalIsOpen"
      class="pointer-events-auto fixed inset-0 z-10 flex h-screen w-screen items-center justify-center bg-purple-500/80"
      @click.self="escapeModals"
    >
      <ConfirmLeaveModal
        v-if="modals.confirmLeave"
        @confirm="onLeaveQueueConfirmed"
        @cancel="onLeaveQueueCancelled"
      />
      <ConfirmKickModal
        v-if="modals.confirmKick"
        :user="modals.confirmKick"
        @confirm="onRemoveUserConfirmed"
        @confirm-block="onBlockUserConfirmed"
        @cancel="onRemoveUserCancelled"
      />
      <ConfirmBanModal
        v-if="modals.confirmBan"
        :user="modals.confirmBan"
        @confirm="onBanUserConfirmed"
        @cancel="onBanUserCancelled"
      />
      <ConfirmCloseModal
        v-if="modals.confirmClose"
        @confirm="onCloseIslandConfirmed"
        @cancel="onCloseIslandCancelled"
      />
      <ReportUserModal
        v-if="modals.reportUser"
        :visit="modals.reportUser"
        @confirm="onReportUserConfirmed"
        @cancel="onReportUserCancelled"
      />
      <RateVisitModal v-if="modals.rateVisit" :island="island" @confirm="onRatingConfirmed" />
      <ShareIslandModal
        v-if="modals.shareIsland"
        :island="island"
        @cancel="onShareIslandCancelled"
      />
      <UpdateIslandModal
        v-if="modals.updateIsland"
        :island="island"
        @confirm="onUpdateIslandConfirmed"
        @cancel="onUpdateIslandCancelled"
      />
      <GetAnnouncementModal
        v-if="modals.getAnnouncement"
        :island="island"
        :host-username="hostUsername"
        @cancel="onGetAnnouncementClosed"
      />
      <SetAnnouncementModal
        v-if="modals.setAnnouncement"
        :announcement="island.announcement || ''"
        @confirm="onSetAnnouncementConfirmed"
        @cancel="onSetAnnouncementCancelled"
      />
    </div>

    <main class="flex w-full flex-1 flex-col justify-center gap-3 py-6 text-center" v-if="!island">
      <h1 class="font-display text-4xl font-bold text-white">Turnip Code Not Found</h1>
      <p>Sorry! The turnip code you've provided is invalid.</p>
      <p>
        It may be a mis-spelled code, or the host may have stopped accepting visitors at this time.
      </p>
    </main>
    <main class="flex min-h-95 w-full flex-1 flex-col lg:flex-row" v-else>
      <!-- Left Column -->
      <div class="bg-tan-800 max-w-8xl relative flex min-h-95 w-full flex-col overflow-hidden">
        <div class="flex justify-between">
          <div
            class="corner-squircle self-start rounded-br-xl px-6 py-2 font-bold tracking-widest text-black uppercase"
            :class="typeColors[island.type]"
          >
            {{ typeName }}
          </div>
          <div
            class="corner-squircle self-start rounded-bl-xl bg-orange-300 px-6 py-2 font-bold tracking-widest text-black"
            v-if="hasModifiedSwitch"
          >
            Modified Switch
          </div>
        </div>

        <div
          class="flex flex-1 flex-col gap-6 border-l-8 p-4 md:border-l-[1.5rem] md:px-12 md:py-8"
          :class="typeBorders[island.type]"
        >
          <!-- Header -->
          <div class="flex flex-col items-center gap-4 lg:flex-row">
            <div class="flex items-center gap-4">
              <h1 class="font-display text-4xl font-bold text-white">{{ island.name }}</h1>
              <h2 class="font-display text-xl font-bold">
                By
                <a
                  class="text-tan-300 hover:text-blue-400"
                  target="_blank"
                  :href="`/user/${island.hostId}`"
                  >{{ hostUsername }}</a
                >
              </h2>
            </div>
            <div v-if="isIslandAdmin" class="flex flex-1 flex-col justify-end gap-4 lg:flex-row">
              <button
                @click="onCloseIslandClicked"
                class="corner-squircle border-base-orange bg-base-purple-500 text-md text-base-orange hover:bg-base-orange flex cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border px-2.5 py-2 font-semibold transition-all hover:text-purple-500"
              >
                <i-fa7-solid-circle-xmark class="size-6" />
                <p>Stop Hosting</p>
              </button>
              <button
                @click="onUpdateIslandClicked"
                class="corner-squircle text-md flex cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border-yellow-400 bg-yellow-400 px-2.5 py-2 font-semibold text-purple-500 transition-all hover:bg-yellow-500"
              >
                <i-fa7-solid-pencil class="size-6" />
                <p>Edit</p>
              </button>
              <button
                @click="onShareIslandClicked"
                class="corner-squircle text-md flex cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border-yellow-400 bg-yellow-400 px-2.5 py-2 font-semibold text-purple-500 transition-all hover:bg-yellow-500"
              >
                <i-fa7-solid-share class="size-6" />
                <p>Share</p>
              </button>
              <button
                v-if="canBanUsers"
                @click="onBanUserRequested(island.hostId, hostUsername)"
                class="corner-squircle border-base-orange bg-base-purple-500 text-md text-base-orange hover:bg-base-orange flex cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border px-2.5 py-2 font-semibold transition-all hover:text-purple-500"
              >
                <i-fa7-solid-circle-xmark class="size-6" />
                <p>Ban Host</p>
              </button>
            </div>
          </div>

          <!-- Bells & Icons -->
          <div class="flex items-center gap-6">
            <!-- todo: is this only supposed to show when island.type is turnips? -->
            <div v-if="island.turnipPrice" class="flex items-center gap-2">
              <img src="/images/icons/turnip.svg" alt="Turnip" class="size-6 lg:size-8" />
              <span class="text-xl font-bold text-white lg:text-2xl"
                >{{ clampTurnipPrice(island.turnipPrice) }} Bells</span
              >
            </div>
            <!-- Using cherry.svg as requested/substituted -->
            <div class="flex items-center gap-2">
              <img :src="`/images/icons/${island.fruit}.svg`" :alt="fruitName" class="size-8" />
              <img
                v-for="icon in npcIcons"
                :key="`${icon.alt}:${icon.path}`"
                :src="icon.path"
                :alt="icon.alt"
                class="size-8"
              />
            </div>
          </div>

          <!-- Entry Fee & Time -->
          <div class="text-base-tan flex flex-col gap-1 text-lg font-medium">
            <div
              v-if="bellsFee || nmtFee"
              class="flex flex-row items-center gap-1 align-middle font-medium"
            >
              <template v-if="bellsFee">
                <span class="text-tan-300"> {{ bellsFee }}</span>
                <img class="size-6" alt="Bells" src="/images/icons/bells.svg" />
              </template>
              <span v-if="bellsFee && nmtFee" class="text-tan-300"> or </span>
              <template v-if="nmtFee">
                <span class="text-tan-300">{{ nmtFee }}</span>
                <img class="size-6" alt="NMT" src="/images/icons/nmt.svg" />
              </template>
              <span v-if="bellsFee || nmtFee" class="text-tan-300"> to Enter </span>
            </div>
            <div class="text-tan-300 flex items-center gap-3 text-sm font-medium lg:text-lg">
              <i-fa7-solid-clock class="size-5 lg:size-8" />
              <span>{{ getIslandTime(island.islandTime) }}</span>
              <span class="text-base-tan">[{{ hemisphereName }}]</span>
            </div>
          </div>

          <!-- Description -->
          <p class="text-tan-300 max-h-60 flex-1 overflow-y-auto leading-relaxed">
            {{ island.description }}
          </p>

          <div class="flex flex-row items-center gap-6 self-stretch">
            <div class="flex flex-row items-center gap-2">
              <i-te-crowd class="size-6" />
              <p class="text-tan-100 text-lg font-medium">
                <span class="text-tan-50">{{ visitors.length }}</span>
                /{{ island.islandLimit }}
              </p>
            </div>

            <div class="flex flex-row items-center gap-2">
              <i-fa7-solid-hourglass-start class="size-6" />
              <p class="text-tan-100 text-lg font-medium">
                <span class="text-tan-50">{{ usersInLine.length }}</span
                >/{{ island.queueLimit }}
              </p>
            </div>

            <!-- Rating -->
            <StarRatingDisplay
              class="flex flex-1 flex-row justify-end"
              :size="6"
              :rating="rating"
            />
          </div>
        </div>
      </div>

      <!-- Right Column: Queue (Flexible) -->
      <div
        class="flex min-h-95 flex-1 flex-col gap-4 overflow-hidden bg-purple-500 px-12 pt-18 pb-6 md:min-w-120"
      >
        <div v-if="isIslandAdmin" class="flex min-h-0 flex-col gap-4">
          <div class="flex flex-row items-center">
            <button
              class="corner-l-squircle text-md flex w-1/2 cursor-pointer items-center justify-center gap-2 self-stretch rounded-l-lg border border-yellow-400 px-2.5 py-2 font-semibold transition-all"
              :class="
                hostTabIndex === 0
                  ? 'bg-yellow-400 text-purple-500 hover:bg-yellow-500'
                  : 'bg-purple-500 text-yellow-400'
              "
              @click="() => (hostTabIndex = 0)"
            >
              <p>In Queue <span v-if="island.status === 'paused'">(Locked)</span></p>
            </button>
            <button
              @click="() => (hostTabIndex = 1)"
              class="corner-r-squircle text-md flex w-1/2 cursor-pointer items-center justify-center gap-2 self-stretch rounded-r-lg border border-yellow-400 px-2.5 py-2 font-semibold transition-all"
              :class="
                hostTabIndex === 1
                  ? 'bg-yellow-400 text-purple-500 hover:bg-yellow-500'
                  : 'bg-purple-500 text-yellow-400 hover:text-white'
              "
            >
              <p>Past Visits</p>
            </button>
          </div>
          <div v-if="hostTabIndex === 0" class="flex min-h-0 flex-col gap-2">
            <div class="flex flex-col items-center gap-2">
              <button
                @click="onLockQueueClicked"
                :disabled="isRelockPrevented"
                class="corner-squircle text-md flex w-full cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border border-yellow-400 bg-purple-500 px-2.5 py-2 font-semibold text-yellow-400 transition-all hover:text-white disabled:cursor-default disabled:opacity-50"
              >
                <template v-if="lockChangeWorking">
                  <i-fa7-solid-spinner class="size-6" />
                  <p>Working...</p>
                </template>
                <template v-else-if="island.status === 'open'">
                  <i-fa7-solid-lock class="size-6" />
                  <p>{{ isRelockPrevented ? 'Wait to ' : '' }}Lock Queue</p>
                </template>
                <template v-else-if="island.status === 'paused'">
                  <i-fa7-solid-lock-open class="size-6" />
                  <p>{{ isRelockPrevented ? 'Wait to ' : '' }}Unlock Queue</p>
                </template>
              </button>
              <button
                @click="onSetAnnouncementClicked"
                class="corner-squircle text-md flex w-full cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border border-yellow-400 bg-purple-500 px-2.5 py-2 font-semibold text-yellow-400 transition-all hover:text-white disabled:cursor-default disabled:opacity-50"
              >
                <i-fa7-solid-bullhorn class="size-6" />
                <p>Announcement</p>
              </button>
            </div>
            <ManageVisitors
              :island-id="island.islandId"
              :visitors="visitors"
              @kick="onRemoveUserRequested"
              @refresh="() => refetch()"
            />
          </div>
          <div v-else-if="hostTabIndex === 1" class="flex min-h-0 flex-col gap-2">
            <PastVisitors
              :visits="pastVisits"
              @rate="onRateVisitClicked"
              @report="onReportUserClicked"
            />
          </div>
        </div>

        <div v-else class="flex flex-col gap-2">
          <div
            v-if="island.announcement !== ''"
            class="bg-tan-800 text-base-tan flex flex-row gap-3 rounded-lg p-3"
          >
            <i-fa7-solid-bullhorn class="size-6" />
            <p>{{ island.announcement }}</p>
          </div>
          <div class="flex flex-row items-center gap-2">
            <button
              @click="onLeaveQueueClicked"
              v-if="currentPosition || isEligible"
              class="corner-squircle flex cursor-pointer items-center justify-center self-stretch rounded-lg px-1.5 py-1 text-lg font-semibold text-yellow-400 hover:text-yellow-500"
            >
              <img src="/images/icons/exit.svg" alt="Leave Queue" class="size-8" />
            </button>
            <p v-if="dodoCode" class="text-tan-100 text-lg font-medium">
              {{ dodoCode }}
            </p>
            <button
              class="corner-squircle text-md flex cursor-pointer items-center justify-center self-stretch rounded-lg bg-green-500 px-2.5 py-2 font-semibold text-black"
              v-else-if="isEligible"
              @click="onShowDodoCodeClicked"
            >
              <img src="/images/icons/dodo.svg" alt="Dodo" class="size-6" />
              <p>Show Dodo Code</p>
            </button>
            <button
              v-else
              @click="onJoinQueueClicked"
              class="corner-squircle text-md flex items-center justify-center self-stretch rounded-lg border border-yellow-400 px-2.5 py-2 font-semibold"
              :disabled="!!currentPosition || isRejoinPrevented || island.status === 'paused'"
              :class="[
                currentPosition || isRejoinPrevented || island.status === 'paused'
                  ? 'cursor-not-allowed bg-purple-500 text-yellow-400'
                  : 'cursor-pointer bg-yellow-400 text-purple-500 hover:bg-yellow-500',
              ]"
            >
              <p>
                <span v-if="isRejoinPrevented">Wait to Rejoin</span>
                <span v-else-if="island.status === 'paused'" class="flex-rot flex gap-2">
                  <i-fa7-solid-lock class="inline size-6" />
                  <p>Queue Locked</p>
                </span>
                <span v-else-if="!currentPosition"
                  >Join Queue - {{ usersInLine.length }} in Line</span
                >
                <span v-else>In Queue - {{ Math.max(currentPosition - 1, 0) }} in Front</span>
              </p>
            </button>
          </div>

          <!-- Queue List -->
          <div class="max-h-80 flex-1 space-y-2 overflow-y-auto">
            <div
              v-for="visitor in visitors"
              :key="visitor.userId"
              class="flex items-center text-lg"
            >
              <span class="mr-1">
                <span v-if="visitor.claimedAt">🏝️</span>
                <span v-else>✈️</span>
              </span>

              <span class="font-bold text-white">{{ visitor.name }}</span>
              <span class="text-tan-400 ml-2 italic">({{ timeAgo(visitor.updatedAt) }})</span>
            </div>
          </div>
        </div>
      </div>
    </main>
    <!-- Ad Banner Area -->
    <div class="bg-tan-200 flex w-full justify-center px-6 py-12">
      <BannerAd />
    </div>

    <Footer />
    <AnonymousLoginModal v-if="modals.anonymousLogin" @close="modals.anonymousLogin = false" />
  </div>
</template>
